/**
 * La liquidación con el técnico: todo lo que se le debe, de una vez.
 *
 * POR QUE NO ALCANZABA CON PAGAR DE A UNA
 * ---------------------------------------
 * Al tercerizado no se le paga equipo por equipo: se le paga cada tanto, por
 * todo lo que se junto. El cartel de la pantalla ya decia cuanto, pero saldarlo
 * eran seis dialogos, seis veces elegir la misma caja y seis chances de
 * quedarse a la mitad sin saber por donde se iba.
 *
 * LAS TRES COSAS QUE TIENE QUE HACER
 * ----------------------------------
 *  1. Mostrar DE DONDE sale el numero. Un total sin desglose no se puede
 *     chequear contra lo que dice el tecnico por telefono; el desglose sí, y
 *     ademas deja destildar la que el tecnico todavia no cobro.
 *  2. Dejar pagar con varias cajas a la vez. Es el `EditorPagos` de siempre, el
 *     mismo del ticket y del cobro de cuenta corriente.
 *  3. Cerrar o no cerrar. El boton se habilita cuando lo cargado da exactamente
 *     lo que se debe; mientras tanto dice cuanto falta o cuanto sobra.
 *
 * DOS MONEDAS, DOS BLOQUES
 * ------------------------
 * Se deben dolares y pesos al mismo tiempo. Cada bloque tiene su desglose y sus
 * cajas, y nada se convierte salvo que a proposito se elija pagar la deuda en
 * dolares desde una caja en pesos (ahi entra la cotizacion, igual que en el
 * pago de una orden sola).
 *
 * EL AJUSTE AUTOMATICO, Y HASTA DONDE LLEGA
 * -----------------------------------------
 * Mientras el bloque tenga UNA sola fila de pago, destildar una orden le baja
 * el importe solo: es el 95% de las veces y evita rehacer la cuenta a mano. Con
 * dos o mas filas la persona ya repartio a proposito y no se le toca nada; se
 * le dice cuanto falta y decide ella.
 */
import { useMemo, useState } from "react";
import { convertir, formatear, type Moneda } from "@tora/shared";

import { EditorPagos } from "@/components/cobro/EditorPagos";
import {
  cuentaPorDefecto,
  mediosDe,
  totalCobrado,
  type CuentaFinanciera,
  type PagoCobro,
} from "@/components/cobro/logica";
import { ChipMoneda, Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero, type MedioPago, type PagoTecnicoLote } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

import { deudaPorMoneda, nombreOrden, type GrupoDeuda, type OrdenTaller } from "./logica";

export interface DialogLiquidarTecnicoProps {
  abierto: boolean;
  /** Todas las órdenes de la pantalla: el diálogo se queda con las que deben. */
  ordenes: ReadonlyArray<OrdenTaller>;
  cuentas: CuentaFinanciera[];
  /**
   * El dólar del TÉCNICO que se propone: el de referencia sin el ajuste fijo
   * del mostrador, el mismo que usa `pagar_tecnico_lote()` por defecto
   * (migración 0054). Es editable: manda lo que se pactó, no la tabla.
   */
  cotizacion: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (datos: PagoTecnicoLote) => void;
}

const NOMBRE_MONEDA: Record<Moneda, string> = { USD: "Dólares", ARS: "Pesos" };

const redondear = (v: number): number => Math.round(v * 100) / 100;

function filaInicial(
  cuentas: CuentaFinanciera[],
  moneda: Moneda,
  total: number,
  cotizacion: number,
): PagoCobro[] {
  // Siempre la caja en pesos, tambien para la deuda en dolares: al tecnico se
  // le paga en pesos. Se puede cambiar a mano si alguna vez se le dan billetes.
  const cuenta = cuentaPorDefecto(cuentas);
  if (!cuenta) return [];
  return [
    {
      id: crypto.randomUUID(),
      cuenta_id: cuenta.id,
      medio: mediosDe(cuenta)[0]?.valor ?? "OTRO",
      importe: redondear(convertir(total, moneda, cuenta.moneda, cotizacion)),
    },
  ];
}

export function DialogLiquidarTecnico({
  abierto,
  ordenes,
  cuentas,
  cotizacion,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogLiquidarTecnicoProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent tamano="lg" mobile="hoja">
        {/* El contenido se monta recien al abrirse: asi el estado inicial
            (que ordenes vienen tildadas, con cuanto arranca cada caja) se
            calcula una vez, en el `useState`, y no hace falta un efecto que
            lo resincronice. */}
        {abierto ? (
          <Contenido
            ordenes={ordenes}
            cuentas={cuentas}
            cotizacion={cotizacion}
            guardando={guardando}
            onCerrar={onCerrar}
            onConfirmar={onConfirmar}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Contenido({
  ordenes,
  cuentas,
  cotizacion,
  guardando,
  onCerrar,
  onConfirmar,
}: Omit<DialogLiquidarTecnicoProps, "abierto">): JSX.Element {
  const grupos = useMemo(() => deudaPorMoneda(ordenes), [ordenes]);

  const [elegidas, setElegidas] = useState<Set<number>>(
    () => new Set(grupos.flatMap((g) => g.ordenes.map((o) => o.id))),
  );
  const [pagos, setPagos] = useState<Partial<Record<Moneda, PagoCobro[]>>>(() =>
    Object.fromEntries(
      grupos.map((g) => [g.moneda, filaInicial(cuentas, g.moneda, g.total, cotizacion)]),
    ),
  );
  /**
   * El cambio con el que se cierra ESTA liquidación.
   *
   * Arranca en el del técnico pero se puede pisar: si se arregló a otro número
   * —un cambio pactado, el del día en que el técnico entregó y no el de hoy—
   * vale lo que se pactó. Viaja en el payload, así la base asienta el egreso
   * con el mismo número que mostró la pantalla en vez de recalcularlo.
   */
  const [cotiz, setCotiz] = useState<number | null>(cotizacion > 0 ? cotizacion : null);
  const cambio = cotiz ?? cotizacion;

  /** Vuelve a proponer una fila única cuando cambia lo que hay que cubrir. */
  function reproponer(grupo: GrupoDeuda, seleccion: Set<number>, conCambio: number): void {
    const filas = pagos[grupo.moneda] ?? [];
    const fila = filas[0];
    if (filas.length !== 1 || !fila) return;
    const total = totalDe(grupo, seleccion);
    const cuenta = cuentas.find((c) => c.id === fila.cuenta_id);
    setPagos((previos) => ({
      ...previos,
      [grupo.moneda]: [
        {
          ...fila,
          importe:
            total <= 0
              ? null
              : redondear(
                  cuenta ? convertir(total, grupo.moneda, cuenta.moneda, conCambio) : total,
                ),
        },
      ],
    }));
  }

  /** Lo que se debe en un bloque contando solo lo tildado. */
  function totalDe(grupo: GrupoDeuda, seleccion: Set<number>): number {
    return redondear(
      grupo.ordenes
        .filter((o) => seleccion.has(o.id))
        .reduce((t, o) => t + aNumero(o.costo_tecnico_importe), 0),
    );
  }

  function alternar(grupo: GrupoDeuda, id: number): void {
    const proxima = new Set(elegidas);
    if (proxima.has(id)) proxima.delete(id);
    else proxima.add(id);
    setElegidas(proxima);
    // Una sola fila: se ajusta sola. Dos o mas: las repartió la persona.
    reproponer(grupo, proxima, cambio);
  }

  /** Mover el cambio rehace las filas únicas: son las que propuso el diálogo. */
  function cambiarCotizacion(valor: number | null): void {
    setCotiz(valor);
    if (valor === null || valor <= 0) return;
    for (const g of grupos) reproponer(g, elegidas, valor);
  }

  const bloques = grupos.map((g) => {
    const total = totalDe(g, elegidas);
    const filas = pagos[g.moneda] ?? [];
    const cargado = redondear(totalCobrado(filas, cuentas, g.moneda, cambio));
    const diferencia = redondear(total - cargado);
    // Un centavo de tolerancia, el mismo que valida la base: el redondeo de la
    // conversion no puede trabar una liquidación que en la práctica cierra.
    const cierra = total <= 0 ? filas.every((f) => !f.importe) : Math.abs(diferencia) <= 0.01;
    return { grupo: g, total, filas, diferencia, cierra };
  });

  // El campo del cambio solo tiene sentido si hay algo que convertir.
  const hayDolares = grupos.some((g) => g.moneda === "USD");
  const cuantas = elegidas.size;
  const puede = cuantas > 0 && bloques.every((b) => b.cierra) && !guardando;

  function confirmar(): void {
    const pagosPayload = bloques.flatMap((b) =>
      b.total <= 0
        ? []
        : b.filas
            .filter((f) => f.importe !== null && f.importe > 0)
            .map((f) => ({
              cuenta_id: f.cuenta_id,
              medio: f.medio as MedioPago,
              importe: f.importe as number,
              moneda_deuda: b.grupo.moneda,
            })),
    );
    onConfirmar({
      ordenes: [...elegidas],
      pagos: pagosPayload,
      ...(cotiz !== null && cotiz > 0 ? { cotizacion: cotiz } : {}),
    });
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Pagarle al técnico</DialogTitle>
        <DialogDescription>
          Todo lo que quedó sin asentar, de una vez. Se registra un egreso por orden y las órdenes
          quedan saldadas juntas: si algo falla, no se paga ninguna.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {/* El cambio va arriba y no adentro de un bloque: decide los importes
            de los dos, y moverlo despues de repartir a mano seria una sorpresa. */}
        {hayDolares ? (
          <Card variant="muted" padding="compacto" className="space-y-1">
            <InputImporte
              valor={cotiz}
              onValorChange={cambiarCotizacion}
              moneda="ARS"
              label="Dólar del técnico"
              ayuda="El de referencia sin el ajuste fijo del mostrador. Cambialo si arreglaste a otro."
            />
          </Card>
        ) : null}

        {bloques.map(({ grupo, total, filas, diferencia, cierra }) => (
          <Card key={grupo.moneda} as="section" padding="normal" className="space-y-3">
            <TituloSeccion nivel="etiqueta" acciones={<ChipMoneda moneda={grupo.moneda} />}>
              {NOMBRE_MONEDA[grupo.moneda]}
            </TituloSeccion>

            {/* El desglose: de donde sale cada peso del total. */}
            <ul className="divide-y divide-border">
              {grupo.ordenes.map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-3 py-1">
                  <Checkbox
                    checked={elegidas.has(o.id)}
                    onChange={() => alternar(grupo, o.id)}
                    className="min-w-0 flex-1"
                    label={
                      <span className="flex flex-wrap items-baseline gap-x-2">
                        <Codigo className="text-xs">{nombreOrden(o)}</Codigo>
                        <span className="truncate font-normal">{o.equipo}</span>
                      </span>
                    }
                    ayuda={[
                      o.tercerizado,
                      o.fecha_entrega
                        ? `entregada el ${formatearFecha(o.fecha_entrega)}`
                        : `entró el ${formatearFecha(o.fecha_ingreso)}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                  <Importe
                    monto={aNumero(o.costo_tecnico_importe)}
                    moneda={grupo.moneda}
                    codigo={false}
                    tamano="sm"
                    className="shrink-0"
                  />
                </li>
              ))}
            </ul>

            <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2">
              <span className="text-sm font-medium">
                {`${cuantasDe(grupo, elegidas)} de ${grupo.ordenes.length}`}
              </span>
              <Importe
                monto={total}
                moneda={grupo.moneda}
                codigo={false}
                tamano="lg"
                className="font-semibold"
              />
            </div>

            {total > 0 ? (
              <>
                {/* De dónde sale el número en pesos. Sin esta línea, el importe
                    que propone la caja es un número que hay que creerle. */}
                {grupo.moneda === "USD" && cambio > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {`A ${formatear(cambio, "ARS")} por dólar son ${formatear(redondear(total * cambio), "ARS")}.`}
                  </p>
                ) : null}
                <EditorPagos
                  pagos={filas}
                  onChange={(p) => setPagos({ ...pagos, [grupo.moneda]: p })}
                  cuentas={cuentas}
                  objetivo={total}
                  monedaObjetivo={grupo.moneda}
                  cotizacion={cambio}
                  permitirVacio
                  etiquetaVacio="¿De qué caja sale?"
                />
                {!cierra ? (
                  <Aviso tono="warning" compacto>
                    {diferencia > 0
                      ? `Faltan ${formatear(diferencia, grupo.moneda)} para cerrar este bloque.`
                      : `Sobran ${formatear(-diferencia, grupo.moneda)} en este bloque.`}
                  </Aviso>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Sin nada tildado en {NOMBRE_MONEDA[grupo.moneda].toLowerCase()}: este bloque no se
                paga.
              </p>
            )}
          </Card>
        ))}

        <p className="text-xs text-muted-foreground">
          Al técnico se le paga en pesos: lo que se debe en dólares se convierte al dólar de
          referencia sin el ajuste fijo del mostrador. Cada orden queda con su propio egreso atado,
          como cuando se paga de a una. Una orden que no entra entera en una caja se parte entre
          dos, y los movimientos suman exacto lo que pusiste.
        </p>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button type="button" cargando={guardando} disabled={!puede} onClick={confirmar}>
          {cuantas === 1 ? "Pagar 1 orden" : `Pagar ${cuantas} órdenes`}
        </Button>
      </DialogFooter>
    </>
  );
}

function cuantasDe(grupo: GrupoDeuda, elegidas: Set<number>): string {
  const n = grupo.ordenes.filter((o) => elegidas.has(o.id)).length;
  return n === 1 ? "1 orden" : `${n} órdenes`;
}
